import { useCallback, useEffect, useRef, useState } from 'react';
import {
    loadFromInstanceStorage,
    removeFromInstanceStorage,
    saveToInstanceStorage,
} from '../../Helpers/instanceStorage';
import {
    CompletionDraftValues,
    EMPTY_COMPLETION_DRAFT,
} from '../../Pages/WorkOrders/components/shared/completionValidation';

export type CompletionAutosaveState = 'idle' | 'saving' | 'saved';

export type CompletionDraftPatch =
    | Partial<CompletionDraftValues>
    | ((prev: CompletionDraftValues) => CompletionDraftValues);

export interface UseCompletionDraftReturn {
    values: CompletionDraftValues;
    setValues: (patch: CompletionDraftPatch) => void;
    clear: () => void;
    /** Drives the "Sparas automatiskt → Sparar… → Sparat" indicator. */
    autosave: CompletionAutosaveState;
}

/** Debounce before a change is written to localStorage. */
const SAVE_DEBOUNCE_MS = 500;
/** How long "Sparat" is shown before the indicator rests on "Sparas automatiskt". */
const SAVED_VISIBLE_MS = 2000;

const STORAGE_PREFIX = 'completionDraft:';

function isDraftShape(value: unknown): value is Partial<CompletionDraftValues> {
    return Boolean(value) && typeof value === 'object';
}

function readDraft(storageKey: string | null): CompletionDraftValues {
    if (storageKey === null) return EMPTY_COMPLETION_DRAFT;
    try {
        const stored = loadFromInstanceStorage<unknown>(STORAGE_PREFIX + storageKey, null);
        if (!isDraftShape(stored)) return EMPTY_COMPLETION_DRAFT;
        return {
            comment: typeof stored.comment === 'string' ? stored.comment : '',
            conditions: isDraftShape(stored.conditions) ? stored.conditions as CompletionDraftValues['conditions'] : {},
            actualCost: typeof stored.actualCost === 'number' ? stored.actualCost : null,
            actualCostInvestmentPercentage: typeof stored.actualCostInvestmentPercentage === 'number'
                ? stored.actualCostInvestmentPercentage
                : null,
        };
    } catch (error) {
        console.warn('Failed to read completion draft:', error);
        return EMPTY_COMPLETION_DRAFT;
    }
}

function writeDraft(storageKey: string, values: CompletionDraftValues): void {
    try {
        saveToInstanceStorage<CompletionDraftValues>(STORAGE_PREFIX + storageKey, values);
    } catch (error) {
        console.warn('Failed to save completion draft:', error);
    }
}

function removeDraft(storageKey: string): void {
    try {
        removeFromInstanceStorage(STORAGE_PREFIX + storageKey);
    } catch (error) {
        console.warn('Failed to remove completion draft:', error);
    }
}

/**
 * Per-device draft of the completion form (comment, conditions, cost) so a
 * half-filled completion survives reloads. Writes are debounced; the returned
 * `autosave` state goes idle → saving → saved (→ idle after a short while).
 *
 * `storageKey` scopes the draft (e.g. the work-order key, or token + key for
 * guests). `null` keeps the draft in memory only, with no indicator changes.
 */
export function useCompletionDraft(storageKey: string | null): UseCompletionDraftReturn {
    const [values, setValuesState] = useState<CompletionDraftValues>(() => readDraft(storageKey));
    const [autosave, setAutosave] = useState<CompletionAutosaveState>('idle');

    // Skip the persist effect on mount and right after a key change/clear:
    // only user edits should flip the indicator.
    const dirtyRef = useRef(false);
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Re-hydrate when the draft scope changes (another work order opened).
    const prevKey = useRef(storageKey);
    useEffect(() => {
        if (prevKey.current === storageKey) return;
        prevKey.current = storageKey;
        dirtyRef.current = false;
        setValuesState(readDraft(storageKey));
        setAutosave('idle');
    }, [storageKey]);

    useEffect(() => {
        if (!dirtyRef.current || storageKey === null) return;
        setAutosave('saving');
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            saveTimer.current = null;
            writeDraft(storageKey, values);
            setAutosave('saved');
            if (savedTimer.current) clearTimeout(savedTimer.current);
            savedTimer.current = setTimeout(() => {
                savedTimer.current = null;
                setAutosave('idle');
            }, SAVED_VISIBLE_MS);
        }, SAVE_DEBOUNCE_MS);
    }, [values, storageKey]);

    // Flush a pending write on unmount so a quick navigation doesn't lose the edit.
    const latest = useRef({ values, storageKey });
    latest.current = { values, storageKey };
    useEffect(() => () => {
        if (savedTimer.current) clearTimeout(savedTimer.current);
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            if (latest.current.storageKey !== null && dirtyRef.current) {
                writeDraft(latest.current.storageKey, latest.current.values);
            }
        }
    }, []);

    const setValues = useCallback((patch: CompletionDraftPatch) => {
        dirtyRef.current = true;
        setValuesState(prev => (typeof patch === 'function' ? patch(prev) : { ...prev, ...patch }));
    }, []);

    const clear = useCallback(() => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
            saveTimer.current = null;
        }
        if (savedTimer.current) {
            clearTimeout(savedTimer.current);
            savedTimer.current = null;
        }
        dirtyRef.current = false;
        if (storageKey !== null) removeDraft(storageKey);
        setValuesState(EMPTY_COMPLETION_DRAFT);
        setAutosave('idle');
    }, [storageKey]);

    return { values, setValues, clear, autosave };
}
